export const caseSignals: string[] = [
  'Severe tooth wear.',
  'Loss of tooth structure.',
  'Functional concerns.',
  'Aesthetic changes.',
  'An extensive rehabilitation.',
];

export const openQuestions: string[] = [
  'What should I evaluate first?',
  'What information do I need to organize?',
  'Which variables deserve closer attention?',
  'What should I document?',
  'What needs to be reviewed before moving forward?',
  'When should I reassess?',
];

export const decisionGaps: string[] = [
  'What comes first?',
  'What comes next?',
  'What needs to be documented?',
  'What should trigger another review?',
];

export const beforeItems: string[] = [
  'You hesitate when organizing complex cases.',
  'You spend hours going back through notes and references.',
  'Every rehabilitation feels like it requires a different process.',
  'You second-guess how to structure your DVO assessment.',
  'You worry about overlooking a variable.',
  'You rely heavily on memory when reviewing cases.',
  'Your clinical information is scattered across multiple places.',
];

export const afterItems: string[] = [
  'You have a structured sequence to follow.',
  'You know which stage of the workflow you are working on.',
  'You can organize relevant information in one place.',
  'You use visual workflow maps instead of trying to remember every step.',
  'You document observations and planning considerations.',
  'You have checkpoints for review and validation.',
  'You can return to the same workflow every time a complex DVO case appears.',
];

export interface Objection {
  emoji: string;
  quote: string;
  answer: string;
}

export const objections: Objection[] = [
  {
    emoji: '🦷',
    quote: 'I have a complex DVO case… where do I even start?',
    answer: 'You know the concepts, but you want a structured sequence for organizing the case.',
  },
  {
    emoji: '🤯',
    quote: 'I know the theory… but applying it is different.',
    answer:
      'The challenge isn’t always knowing more. It’s turning multiple variables into an organized clinical workflow.',
  },
  {
    emoji: '📐',
    quote: 'I’m still second-guessing how I structure my VDO assessment.',
    answer:
      'You want a systematic way to organize the relevant information rather than relying on memory or isolated measurements.',
  },
  {
    emoji: '🔍',
    quote: 'What if I’m missing something?',
    answer:
      'Complex cases contain multiple variables. Without a workflow, important questions can easily be overlooked.',
  },
  {
    emoji: '⏱️',
    quote: 'I keep going back to my notes.',
    answer: 'Books, lectures, saved posts and course materials are scattered everywhere.',
  },
  {
    emoji: '📋',
    quote: 'I wish I had the same process for every complex case.',
    answer:
      'You want a repeatable framework that helps you organize your assessment and planning process.',
  },
];

export const buildingBlocks: string[] = [
  'Workflow Maps',
  'Protocols',
  'Checklists',
  'Decision Points',
  'Case Planning Tools',
  'Quick References',
];

export interface IncludedTool {
  emoji: string;
  title: string;
  description: string;
}

export const includedTools: IncludedTool[] = [
  {
    emoji: '🧭',
    title: 'CLINICAL WORKFLOW MAPS',
    description:
      'Visual sequences designed to help you see where you are in the case and what stage comes next.',
  },
  {
    emoji: '📋',
    title: 'PROFESSIONAL CHECKLISTS',
    description: 'Practical checklists designed to bring consistency to your workflow.',
  },
  {
    emoji: '🔄',
    title: 'WORKFLOW PROTOCOLS',
    description:
      'Structured processes for organizing different stages of assessment and case planning.',
  },
  {
    emoji: '📊',
    title: 'REVIEW MATRICES',
    description: 'Organize variables, priorities and attention points in one place.',
  },
  {
    emoji: '🗂️',
    title: 'CASE PLANNING WORKSHEETS',
    description: 'Document observations, questions, information gaps and planning considerations.',
  },
  {
    emoji: '⚡',
    title: 'QUICK REFERENCE GUIDES',
    description:
      'Condensed resources for fast consultation when you don’t want to search through pages of theory.',
  },
  {
    emoji: '📝',
    title: 'CASE WORKFLOW TEMPLATES',
    description:
      'Ready-to-use templates for applying the same organizational process to future cases.',
  },
];

export const guaranteeSteps: string[] = [
  'Get immediate access.',
  'Open the six workflow volumes.',
  'Build your first DVO workflow.',
  'Use the checklists and workflow maps.',
  'Apply the framework to an educational case.',
  'Review the system and decide whether it fits your workflow.',
];

export interface Faq {
  question: string;
  answer: string;
}

export const faqs: Faq[] = [
  {
    question: 'Is DVO Clinical Workflow Builder™ a course?',
    answer:
      'No. It’s a digital clinical workflow system created by Dr. Karito, containing six workflow volumes, protocols, workflow maps, checklists, matrices, worksheets and practical reference tools.',
  },
  {
    question: 'Who is this for?',
    answer:
      'It is designed for dental professionals who work with or plan complex restorative, rehabilitation and DVO-related cases and want a more structured way to organize their clinical workflow.',
  },
  {
    question: 'Do I need to be a specialist?',
    answer:
      'No. The system is designed as an educational organizational resource. Appropriate use depends on the dentist’s education, training, experience and professional judgment.',
  },
  {
    question: 'Does this replace clinical education?',
    answer:
      'No. It is an educational workflow and organization resource. It does not replace clinical education, professional training, diagnosis or clinical judgment.',
  },
  {
    question: 'How will I receive the product?',
    answer: 'After completing your purchase, you’ll receive digital access to the materials.',
  },
  {
    question: 'Can I download the materials?',
    answer:
      'Yes. The digital resources are designed for convenient digital reference and printing where applicable.',
  },
  { question: 'Is this a one-time payment?', answer: 'Yes.' },
  {
    question: 'Do I get lifetime access?',
    answer: 'Yes. Your purchase includes lifetime access to the current product materials.',
  },
  {
    question: 'What happens if I don’t like it?',
    answer:
      'You have 7 days to review the system. If you decide it isn’t right for you, you can request a refund within the applicable guarantee period.',
  },
  {
    question: 'What currency will I be charged in?',
    answer:
      'The offer is displayed at $27 USD. Currency conversion is handled automatically where applicable.',
  },
];

export const finalQuestions: string[] = [
  'WHAT SHOULD I ORGANIZE FIRST?',
  'WHICH VARIABLES SHOULD I REVIEW?',
  'WHAT INFORMATION AM I MISSING?',
  'WHAT STAGE OF THE WORKFLOW AM I IN?',
  'WHAT SHOULD I REVIEW BEFORE MOVING FORWARD?',
];

export interface PurchaseNotice {
  name: string;
  city: string;
  country: string;
}

export const purchaseNotices: PurchaseNotice[] = [
  { name: 'Dra. Valentina R.', city: 'Bogotá', country: 'Colombia' },
  { name: 'Dr. Andrés M.', city: 'Ciudad de México', country: 'México' },
  { name: 'Dra. Camila S.', city: 'Santiago', country: 'Chile' },
  { name: 'Dr. Joaquín P.', city: 'Buenos Aires', country: 'Argentina' },
  { name: 'Dra. Lucía F.', city: 'Madrid', country: 'España' },
  { name: 'Dr. Ricardo T.', city: 'Lima', country: 'Perú' },
  { name: 'Dra. Mariana G.', city: 'Montevideo', country: 'Uruguay' },
  { name: 'Dr. Esteban V.', city: 'Quito', country: 'Ecuador' },
  { name: 'Dra. Paula C.', city: 'San José', country: 'Costa Rica' },
  { name: 'Dr. Fernando L.', city: 'Guadalajara', country: 'México' },
  { name: 'Dra. Sofía N.', city: 'Asunción', country: 'Paraguay' },
  { name: 'Dr. Gabriel A.', city: 'Barcelona', country: 'España' },
];
